import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Выбор позиции номенклатуры с поиском.
 *
 * Обычный список годился, пока позиций было одиннадцать. На шестидесяти шести
 * он превращается в прокрутку вслепую: чтобы поставить соус, приходится
 * помнить, где он лежит между «Сыром» и «Тестом». Поле с подсказками решает
 * это одним способом на всех экранах — и в накладной, и в техкарте.
 *
 * Список не подменяет ввод: пока в поле пусто, показываются все позиции.
 * Кладовщик, который не помнит названия, должен иметь возможность посмотреть.
 */
export interface PickerItem {
  id: string;
  title: string;
  /** Подпись справа: единица учёта, группа, признак архива. */
  note?: string;
}

export function IngredientPicker({
  items,
  value,
  onChange,
  placeholder = "Начните вводить название",
  disabled = false
}: {
  items: PickerItem[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  const chosen = items.find(item => item.id === value) ?? null;

  const found = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items.slice(0, 40);

    /*
     * Совпадение с начала слова идёт первым: набирая «со», ждут «Соус», а не
     * «Мясо». Внутри слова тоже ищем — название могли запомнить с середины.
     */
    const starts: PickerItem[] = [];
    const inside: PickerItem[] = [];

    for (const item of items) {
      const title = item.title.toLowerCase();
      if (title.startsWith(needle) || title.split(" ").some(word => word.startsWith(needle))) starts.push(item);
      else if (title.includes(needle)) inside.push(item);
    }

    return [...starts, ...inside].slice(0, 40);
  }, [items, query]);

  // Клик мимо закрывает список: иначе он перекрывает соседние строки таблицы.
  useEffect(() => {
    if (!open) return;

    const onDown = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function choose(item: PickerItem) {
    onChange(item.id);
    setQuery("");
    setOpen(false);
  }

  return (
    <div className="picker" ref={box}>
      <input
        className="picker-input"
        value={open ? query : (chosen?.title ?? "")}
        placeholder={chosen ? chosen.title : placeholder}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={event => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={event => {
          if (event.key === "Escape") setOpen(false);
          // Enter выбирает единственное совпадение: набрал «бекон» — и дальше.
          if (event.key === "Enter" && found.length === 1) {
            event.preventDefault();
            choose(found[0]!);
          }
        }}
      />

      {open ? (
        <div className="picker-list">
          {found.length === 0 ? (
            <p className="picker-empty">Ничего не нашлось</p>
          ) : (
            found.map(item => (
              <button
                key={item.id}
                type="button"
                className={`picker-option ${item.id === value ? "active" : ""}`}
                onClick={() => choose(item)}
              >
                <span>{item.title}</span>
                {item.note ? <small>{item.note}</small> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
